import { useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  DetectedWallet,
  useWalletSign,
} from '../../xpx-perps-withdrawal/useWalletSign';
import {
  WithdrawalItem,
  rejectWithdrawal,
} from '../../../services/xbitRelayWithdrawalsService';

type Props = {
  record: WithdrawalItem;
  onClose(): void;
};

const isUserRejection = (err: any): boolean => {
  const code = err?.code ?? err?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED') return true;
  const msg = (err?.message || '').toLowerCase();
  return msg.includes('reject') || msg.includes('denied');
};

const buildMessage = (recordId: string, timestampMs: number) =>
  `Withdrawal ID: ${recordId}\n` +
  `Action: Reject\n` +
  `Timestamp: ${timestampMs}`;

export function RejectDialog({ record, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { detectedWallets, connect, address, signMessage } = useWalletSign();
  const [selectedWallet, setSelectedWallet] = useState<DetectedWallet | null>(
    null,
  );
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');

  const handleConnect = async () => {
    if (!selectedWallet) return;
    setConnecting(true);
    try {
      await connect(selectedWallet.provider);
    } catch (err: any) {
      if (!isUserRejection(err)) {
        notify(err?.message || t('common.error'), { type: 'error' });
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleSignAndReject = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const timestampMs = Date.now();
      const message = buildMessage(record.id, timestampMs);
      const signature = await signMessage(message);
      await rejectWithdrawal(record.id, {
        admin_id: address.toLowerCase(),
        signature,
        timestamp_ms: timestampMs,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      notify(t('module.xbitRelayWithdrawals.rejectSuccess'), {
        type: 'success',
      });
      refresh();
      onClose();
    } catch (err: any) {
      if (isUserRejection(err)) return;
      notify(
        err?.response?.data?.message || err?.message || t('common.error'),
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xbitRelayWithdrawals.rejectTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.userId')}:</b> {record.userId}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.provider')}:</b>{' '}
              {record.provider}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.chainId')}:</b>{' '}
              {record.chainId}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              <b>{t('module.xbitRelayWithdrawals.toAddress')}:</b>{' '}
              {record.toAddress}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.amount')}:</b> {record.amount}{' '}
              {record.symbol || record.tokenSymbol || record.token}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.platformFee')}:</b> {record?.platformFee}{' '}
              {record.symbol || record.tokenSymbol || record.token}
            </Typography>
          </Stack>

          <TextField
            size="small"
            label={t('module.xbitRelayWithdrawals.noteOptional')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            disabled={loading}
          />

          {detectedWallets.length === 0 ? (
            <Typography variant="body2" color="error">
              {t('module.xbitRelayWithdrawals.noWalletFound')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>
                {t('module.xbitRelayWithdrawals.selectWallet')}
              </InputLabel>
              <Select
                label={t('module.xbitRelayWithdrawals.selectWallet')}
                value={selectedWallet?.name ?? ''}
                onChange={(e) => {
                  const w = detectedWallets.find(
                    (x) => x.name === e.target.value,
                  );
                  setSelectedWallet(w ?? null);
                }}
                disabled={Boolean(address) || connecting || loading}
              >
                {detectedWallets.map((w) => (
                  <MenuItem key={w.name} value={w.name}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!address && selectedWallet && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleConnect}
              disabled={connecting}
              startIcon={connecting ? <CircularProgress size={14} /> : null}
            >
              {t('module.xbitRelayWithdrawals.connect')}
            </Button>
          )}

          {address && (
            <Typography variant="caption" color="text.secondary">
              {t('module.xbitRelayWithdrawals.connectedAs')}: {address}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading || connecting}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSignAndReject}
          disabled={!address || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayWithdrawals.signAndReject')}
        </Button>
      </DialogActions>
    </>
  );
}
